/**
 * UGC Instagram Grid for Shopify
 * CSVを読み込み、Instagram URLのみの場合はBFFでサムネ等を自動解決して表示
 */
(function() {
  'use strict';

  const CONFIG = {
    SELECTORS: { grid: '[data-ugc-grid]', status: '[data-ugc-status]' },
    CLASSES: { loaded: 'loaded', visible: 'visible', skeleton: 'ugc-skeleton', error: 'error' },
    TIMING: { fetchTimeout: 8000, retryDelay: 2000, animationDelay: 80, intersectionThreshold: 0.1 },
    BATCH: { resolveSize: 6, resolveDelay: 200 }
  };

  class UGCGrid {
    constructor(element) {
      this.element = element;
      this.statusElement = document.querySelector(CONFIG.SELECTORS.status);
      this.csvUrl = element.dataset.csvUrl;
      this.resolverEndpoint = element.dataset.resolverEndpoint || '/api/ig/resolve-thumbnail';
      this.limit = parseInt(element.dataset.limit, 10) || 12;
      this.columns = {
        desktop: parseInt(element.dataset.columnsDesktop, 10) || 6,
        tablet: parseInt(element.dataset.columnsTablet, 10) || 4,
        mobile: parseInt(element.dataset.columnsMobile, 10) || 3
      };
      this.observer = null;
      this.abortController = null;
      this.retryCount = 0;
      this.maxRetries = 3;
      this.init();
    }

    init() { this.setupIntersectionObserver(); this.loadData(); }

    setStatus(text, isError = false) {
      if (this.statusElement) {
        this.statusElement.textContent = `status: ${text}`;
        this.statusElement.classList.toggle(CONFIG.CLASSES.error, isError);
      }
    }

    setupIntersectionObserver() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add(CONFIG.CLASSES.visible);
              this.observer.unobserve(entry.target);
            }
          });
        }, { threshold: CONFIG.TIMING.intersectionThreshold, rootMargin: '50px' });
      }
    }

    createSkeletonLoader(count = this.limit) {
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < count; i++) {
        const skeleton = document.createElement('div');
        skeleton.className = `ugc-card ${CONFIG.CLASSES.skeleton}`;
        skeleton.style.transitionDelay = `${i * 50}ms`;
        skeleton.setAttribute('aria-hidden', 'true');
        fragment.appendChild(skeleton);
      }
      this.element.innerHTML = '';
      this.element.appendChild(fragment);
      requestAnimationFrame(() => {
        this.element.querySelectorAll('.ugc-card').forEach(card => card.classList.add(CONFIG.CLASSES.visible));
      });
    }

    createCard(item, index) {
      const card = document.createElement('article');
      card.className = 'ugc-card';
      card.style.transitionDelay = `${index * CONFIG.TIMING.animationDelay}ms`;
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'button');
      card.setAttribute('aria-label', `投稿を開く: ${item.caption || 'キャプションなし'}`);

      const imageAlt = this.sanitizeText(item.caption || 'UGC投稿画像');
      const safeCaption = this.sanitizeHTML(item.caption || '');
      const safeUser = this.sanitizeHTML(item.user || '');

      card.innerHTML = `
        <img class="ugc-image"
             src="${this.sanitizeURL(item.image)}"
             alt="${imageAlt}"
             loading="lazy"
             decoding="async">
        <div class="ugc-overlay">
          <div class="ugc-caption">${safeCaption}</div>
          <div class="ugc-user">${safeUser}</div>
        </div>
      `;

      const handleActivation = (event) => {
        if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        this.openLink(item.url || item.instagram_url, item.caption);
      };
      card.addEventListener('click', handleActivation);
      card.addEventListener('keydown', handleActivation);

      return card;
    }

    sanitizeHTML(str) { const div = document.createElement('div'); div.textContent = str; return div.innerHTML; }
    sanitizeText(str) { return String(str).replace(/[<>"'&]/g, ''); }
    sanitizeURL(url) { try { return new URL(url).href; } catch { return ''; } }

    openLink(url) {
      if (!url || url === '#') return;
      try { const link = new URL(url); window.open(link.href, '_blank', 'noopener,noreferrer'); } catch {}
    }

    async fetchCSV(url) {
      this.abortController = new AbortController();
      const timeoutId = setTimeout(() => this.abortController.abort(), CONFIG.TIMING.fetchTimeout);
      try {
        const res = await fetch(url, {
          cache: 'no-store',
          signal: this.abortController.signal,
          headers: { 'Accept': 'text/csv,text/plain,*/*' }
        });
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return await res.text();
      } catch (e) {
        clearTimeout(timeoutId);
        throw e;
      }
    }

    parseCSV(text) {
      if (!text || !text.trim()) return [];
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
      const rows = []; let row = []; let current = ''; let inQuotes = false;
      for (let i = 0; i < text.length; i++) {
        const c = text[i], n = text[i + 1];
        if (c === '"') { if (inQuotes && n === '"') { current += '"'; i++; } else { inQuotes = !inQuotes; } }
        else if (c === ',' && !inQuotes) { row.push(current.trim()); current = ''; }
        else if ((c === '\n' || c === '\r') && !inQuotes) { if (c === '\r' && n === '\n') i++; row.push(current.trim()); if (row.some(x => x.length)) rows.push(row); row = []; current = ''; }
        else { current += c; }
      }
      if (current.length > 0 || row.length > 0) { row.push(current.trim()); if (row.some(x => x.length)) rows.push(row); }
      return rows.filter(r => r.length > 0);
    }

    rowsToItems(rows) {
      if (!rows || rows.length === 0) return [];
      const headers = rows[0].map(h => (h || '').trim().toLowerCase());
      const dataRows = rows.slice(1);
      const named = ['url','image','caption','user','instagram_url'];
      const hasNamed = named.some(col => headers.includes(col));
      if (hasNamed) {
        const idx = {
          url: headers.indexOf('url'),
          image: headers.indexOf('image'),
          caption: headers.indexOf('caption'),
          user: headers.indexOf('user'),
          instagram_url: headers.indexOf('instagram_url')
        };
        return dataRows.map(r => ({
          url: idx.url >= 0 ? (r[idx.url] || '').trim() : '',
          instagram_url: idx.instagram_url >= 0 ? (r[idx.instagram_url] || '').trim() : '',
          image: idx.image >= 0 ? (r[idx.image] || '').trim() : '',
          caption: idx.caption >= 0 ? (r[idx.caption] || '').trim() : '',
          user: idx.user >= 0 ? (r[idx.user] || '').trim() : ''
        }));
      }
      return dataRows.map(r => ({ image: (r[0] || '').trim(), url: (r[1] || '').trim(), caption: (r[2] || '').trim(), user: (r[3] || '').trim() }));
    }

    async preloadImages(items) {
      const ps = items.map(item => new Promise(resolve => {
        if (!item.image) return resolve({ ...item, imageLoaded: false });
        const img = new Image(); img.decoding = 'async';
        const t = setTimeout(() => resolve({ ...item, imageLoaded: false }), 5000);
        img.onload = () => { clearTimeout(t); resolve({ ...item, imageLoaded: true }); };
        img.onerror = () => { clearTimeout(t); resolve({ ...item, imageLoaded: false }); };
        img.src = item.image;
      }));
      return Promise.all(ps);
    }

    async resolveInstagramItemIfNeeded(item) {
      const url = (item.url || item.instagram_url || '').trim();
      const isInsta = /https?:\/\/(www\.)?(instagram\.com|instagr\.am)\//i.test(url);
      if (!isInsta || item.image) return item;
      if (!this.resolverEndpoint) return item;
      try {
        const api = `${this.resolverEndpoint}?url=${encodeURIComponent(url)}`;
        const res = await fetch(api, { cache: 'no-store' });
        if (!res.ok) throw new Error(`RESOLVE_HTTP_${res.status}`);
        const data = await res.json();
        return {
          ...item,
          image: data.image || item.image || '',
          caption: item.caption || data.caption || '',
          user: item.user || data.user || '',
          url
        };
      } catch (e) {
        console.warn('Instagram resolve failed:', e);
        return item;
      }
    }

    async resolveBatch(items) {
      let out = [];
      for (let i = 0; i < items.length; i += CONFIG.BATCH.resolveSize) {
        const slice = items.slice(i, i + CONFIG.BATCH.resolveSize);
        const resolvedSlice = [];
        for (const it of slice) { /* eslint-disable no-await-in-loop */
          resolvedSlice.push(await this.resolveInstagramItemIfNeeded(it));
        }
        out = out.concat(resolvedSlice);
        await this.delay(CONFIG.BATCH.resolveDelay);
      }
      return out;
    }

    async render(items) {
      if (!items || items.length === 0) {
        this.element.innerHTML = `<div class="ugc-error">表示するデータがありません。<br>CSV URLとデータ形式を確認してください。</div>`;
        return;
      }
      const validItems = items.filter(item => item.image && item.image.length > 0).slice(0, this.limit);
      if (validItems.length === 0) {
        this.element.innerHTML = `<div class="ugc-error">有効な画像データがありません。<br>画像URLまたはInstagram URLをご確認ください。</div>`;
        return;
      }
      this.setStatus('画像を読み込み中...');
      const loadedItems = await this.preloadImages(validItems);
      this.element.innerHTML = '';
      const fragment = document.createDocumentFragment();
      loadedItems.forEach((item, index) => {
        if (item.imageLoaded) {
          const card = this.createCard(item, index);
          fragment.appendChild(card);
          if (this.observer) this.observer.observe(card);
          else setTimeout(() => card.classList.add(CONFIG.CLASSES.visible), index * CONFIG.TIMING.animationDelay);
        }
      });
      this.element.appendChild(fragment);
      await this.delay(100);
      this.element.classList.add(CONFIG.CLASSES.loaded);
    }

    delay(ms) { return new Promise(r => setTimeout(r, ms)); }

    async loadData() {
      try {
        if (!this.csvUrl || this.csvUrl.trim() === '') {
          this.setStatus('CSV URLが未設定', true);
          this.element.innerHTML = `<div class="ugc-error">CSV URLが設定されていません。<br>公開CSV URLを設定してください。</div>`;
          return;
        }
        this.setStatus('データを取得中...');
        this.createSkeletonLoader();

        const url = this.csvUrl + (this.csvUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
        const csvText = await this.fetchCSV(url);

        this.setStatus('データを解析中...');
        const rows = this.parseCSV(csvText);
        const items = this.rowsToItems(rows);

        this.setStatus('Instagramサムネイルを解決中...');
        const resolved = await this.resolveBatch(items);

        this.setStatus('画像を準備中...');
        await this.render(resolved);

        this.setStatus('読み込み完了');
        this.retryCount = 0;
      } catch (error) {
        console.error('UGC Grid Error:', error);
        let errorMessage = 'データの読み込みに失敗しました';
        if (error.name === 'AbortError') errorMessage = 'リクエストがタイムアウトしました';
        else if (error.message && error.message.includes('HTTP')) errorMessage = `サーバーエラー: ${error.message}`;
        else if (error.message && error.message.includes('Failed to fetch')) errorMessage = 'ネットワークエラーが発生しました';
        this.setStatus(`エラー: ${errorMessage}`, true);
        this.element.innerHTML = `<div class="ugc-error">${errorMessage}<br><small>URL: ${this.csvUrl}</small><br><small>自動的に再試行します...</small></div>`;
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          setTimeout(() => { this.setStatus(`再試行中... (${this.retryCount}/${this.maxRetries})`); this.loadData(); }, CONFIG.TIMING.retryDelay * this.retryCount);
        }
      }
    }

    destroy() { if (this.observer) this.observer.disconnect(); if (this.abortController) this.abortController.abort(); }
  }

  class UGCGridManager {
    constructor() { this.instances = new Map(); this.init(); }
    init() { this.bindEvents(); this.initializeGrids(); }
    bindEvents() {
      document.addEventListener('shopify:section:load', (e) => this.initializeGrids(e.target));
      document.addEventListener('shopify:section:unload', (e) => this.destroyGrids(e.target));
      document.addEventListener('shopify:section:reorder', () => this.reinitializeAllGrids());
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.refreshAllGrids(); });
      let resizeTimeout;
      window.addEventListener('resize', () => { clearTimeout(resizeTimeout); resizeTimeout = setTimeout(() => this.handleResize(), 250); });
    }
    initializeGrids(container = document) {
      const grids = container.querySelectorAll(CONFIG.SELECTORS.grid);
      grids.forEach(grid => {
        const sectionId = this.getSectionId(grid);
        if (!this.instances.has(sectionId)) {
          try { const instance = new UGCGrid(grid); this.instances.set(sectionId, instance); }
          catch (e) { console.error('Failed to initialize UGC Grid:', e); }
        }
      });
    }
    destroyGrids(container) {
      const grids = container.querySelectorAll(CONFIG.SELECTORS.grid);
      grids.forEach(grid => {
        const sectionId = this.getSectionId(grid);
        const instance = this.instances.get(sectionId);
        if (instance) { instance.destroy(); this.instances.delete(sectionId); }
      });
    }
    reinitializeAllGrids() { this.instances.forEach(i => i.destroy()); this.instances.clear(); this.initializeGrids(); }
    refreshAllGrids() { this.instances.forEach(i => { if (i.loadData) { i.retryCount = 0; i.loadData(); } }); }
    handleResize() {}
    getSectionId(el) { const section = el.closest('[data-section-id]'); return section ? section.dataset.sectionId : el.id || 'default'; }
  }

  function initialize() {
    if (!window.fetch) { console.warn('UGC Grid: fetch API not supported'); return; }
    window.ugcGridManager = new UGCGridManager();
    if (window.Shopify && window.Shopify.designMode) window.ugcGridUtils = {};
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();

  window.UGCGrid = UGCGrid;
})();
